<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import SearchComponent from './SearchComponent.vue';

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const searchIsOpen = ref(false);

// Computed properties
const loggedIn = computed(() => store.state.loggedIn);
const retractedSidebar = computed(() => store.state.retractedSidebar);

const menus = computed(() => [
    {
        name: 'Main',
        shown: true,
        routes: [
            { name: t('nav.home'), path: '/', icon: 'fa-home', shown: true },
            { name: t('nav.stafflists.all'), path: '/staff-lists', icon: 'fa-list', shown: true },
            { name: t('nav.stafflists.me'), path: '/staff-lists/me', icon: 'fa-user', shown: loggedIn.value },
            { name: t('nav.reports'), path: '/reports', icon: 'fa-flag', shown: loggedIn.value, desktopOnly: true, ...store.state.reports > 0 && { badge: store.state.reports } },
            { name: t('nav.stafflists.create'), path: '/staff/create/1', icon: 'fa-add', shown: true },
            { name: t('nav.help.guidelines'), path: '/guidelines', icon: 'fa-book', shown: true, desktopOnly: true },
        ]
    },
    {
        name: 'Search',
        shown: true,
        routes: [
            { ...(store.state.searchSystem === 'new' ? { name: t('nav.search'), event: openSearch } : { name: t('footer.navigation.advancedSearch'), path: '/search' }),
            icon: 'fa-search', shown: true },
        ]
    },
    {
        name: 'Account',
        shown: true,
        routes: [
            { name: 'avatar', shown: loggedIn.value },
            { name: t('nav.account.login'), path: '/login.php', event: openLogIn, icon: 'fa-right-to-bracket', shown: !loggedIn.value },
        ]
    }
]);

// Methods
const retractSidebar = () => {
    store.state.retractedSidebar = !store.state.retractedSidebar;
    localStorage.setItem('retractedSidebar', store.state.retractedSidebar);
};
const openLogIn = () => {
    const popup = window.open(`${import.meta.env.VITE_API_URL}/login.php`, '_blank', `width=400,height=600,status=yes`);
    
    const pollTimer = setInterval(() => {
        if (popup.closed === true) {
            clearInterval(pollTimer);
            checkAuth();
        }
    }, 500);
};
const checkAuth = async () => {
    const authRes = await fetch(`${import.meta.env.VITE_API_URL}/api/account/auth.php`);
    if (!authRes) {
        return;
    }

    const loggedInData = await authRes.json();
    store.commit('auth', loggedInData);
};
const openSearch = () => {
    searchIsOpen.value = true;
};

// Close the search overlay when the route changes and clean up the key listener.
watch(() => route.path, () => {
    if (searchIsOpen.value) {
        searchIsOpen.value = false;
    }
});

// Lifecycle hooks
onMounted(() => {
    if (localStorage.getItem('retractedSidebar') === 'true') {
        store.state.retractedSidebar = true;
    }

});
</script>

<template>
    <nav :class="{ 'retracted': retractedSidebar }">
        <section id="separator">
            <span class="hidden md:flex items-center justify-center font-rubik text-xl font-bold text-white text-center h-16">
                <RouterLink v-if="!retractedSidebar" to="/" translate="no">{{ t('app.name') }}</RouterLink>
                <RouterLink v-else to="/" translate="no">{{ t('app.shortName') }}</RouterLink>
            </span>

            <section v-for="menu in menus" :key="menu.name" v-show="menu.shown" :class="{ 'md-only': menu.mdOnly }">
                <template v-for="routeItem in menu.routes" v-show="routeItem.shown">
                    <template v-if="routeItem.shown">
                        <RouterLink to="/account" v-if="routeItem.name === 'avatar'" class="account-area">
                            <img class="avatar" :src="store.state.avatar || '/assets/img/otter_default.png'" :title="t('nav.account.loggedInAs', { name: store.state.name })" />
                            <h4 class="username">{{ store.state.screenName }}</h4>
                        </RouterLink>

                        <a v-else-if="routeItem.event"
                            :href="routeItem.path"
                            @click.prevent="routeItem.event"
                            
                            :to="routeItem.path"
                            class="side-menu"
                            :class="{ 'active': route.path === routeItem.path, 'retracted': retractedSidebar, 'desktop-only': routeItem.desktopOnly }"
                        >
                            <font-awesome-icon :icon="`fa-solid ${routeItem.icon}`" />
                            <span>{{ routeItem.name }}</span>
                        </a>

                        <router-link v-else
                            :to="{path: routeItem.path, force: true}"
                            class="side-menu"
                            :class="{ 'active': route.path === routeItem.path, 'retracted': retractedSidebar, 'desktop-only': routeItem.desktopOnly }"
                        >
                            <div class="absolute flex items-center justify-center w-4 h-4 rounded-full bg-crimson-400 text-white text-[0.6rem]" :class="{'top-0 ml-6': retractedSidebar, 'top-1 ml-2': !retractedSidebar}" v-if="routeItem.badge">{{ routeItem.badge }}</div>
                            <font-awesome-icon :icon="`fa-solid ${routeItem.icon}`" />
                            <span>{{ routeItem.name }}</span>
                        </router-link>
                    </template>
                </template>
            </section>
        </section>

        <button id="retractor" @click="retractSidebar">
            <font-awesome-icon v-if="retractedSidebar" icon="fa-solid fa-angles-right" />
            <font-awesome-icon v-else icon="fa-solid fa-angles-left" />
        </button>
    </nav>

    <SearchComponent v-if="searchIsOpen" :onClose="() => { searchIsOpen = false }" />
</template>

<style scoped>
nav {
    @apply fixed bg-keyframe-600 text-white text-sm shadow z-40
    left-0 bottom-0 w-full
    overflow-hidden
    md:top-0 md:h-[100dvh] md:w-64;
}
nav.retracted {
    @apply md:w-20;
}
nav #separator {
    @apply flex flex-row flex-wrap h-full justify-center
    md:flex-col md:flex-nowrap md:overflow-y-auto md:divide-y md:divide-white md:divide-opacity-10 md:justify-start;
}
nav #separator section.md-only {
    @apply hidden md:flex;
}
nav #separator section {
    @apply flex flex-row justify-center items-center gap-1
    md:flex-col md:p-2 md:gap-0 md:items-stretch;
}
nav.retracted #separator section {
    @apply md:p-0;
}

nav .account-area {
    @apply md:my-3;
}

nav .avatar {
    @apply hidden md:block w-16 h-16 rounded-full border-white border-2 mx-auto;
}
nav.retracted .avatar {
    @apply md:w-12 md:h-12;
}

nav .username {
    @apply hidden text-center font-rubik mt-1
    md:block;
}
nav.retracted .username {
    @apply md:text-xs md:px-2 md:truncate;
}


.side-menu {
    @apply relative flex gap-3 items-center justify-center hover:bg-keyframe-550 focus:bg-keyframe-550 outline-none rounded-full cursor-pointer
    flex-col w-12 h-12 p-0
    md:flex-row md:w-auto md:h-auto md:px-4 md:py-3 md:rounded-md md:justify-normal;
}
.side-menu.retracted {
    @apply md:gap-1 md:flex-col md:items-center md:justify-center md:px-[0.4rem] md:py-3 md:rounded-none;
}
.side-menu.active {
    @apply bg-keyframe-500 text-white;
}

.side-menu span {
    @apply hidden md:block;
}
.side-menu.retracted span {
    @apply md:text-xs md:text-center;
}

#retractor {
    @apply w-10 h-10 rounded-full hover:bg-keyframe focus:bg-keyframe outline-none absolute bottom-2 right-2 text-white cursor-pointer hidden
    md:block;
}
.desktop-only {
    @apply hidden md:flex;
}
</style>